// Rodapé vivo do hub: só dado real já disponível.
// Omitidos de propósito: etapas do Kanban em movimento (stage_stats carrega
// os cards do funil) e "atualizado há X min" da Análise (não há timestamp
// barato de relatórios).
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useReleaseNotes } from 'dashboard/composables/useReleaseNotes';
import { waitingMinutesFromEpoch } from 'dashboard/helper/waitingLabel';
import AiCenterAPI from 'dashboard/api/aiCenter';
import {
  loadHubCampaigns,
  loadHubFlows,
  loadHubTaskNotifications,
  refreshHubConversationStats,
} from './useHubOverview';
import { useHubDataAccess } from './hubDataAccess';

import { ATTENTION_HEALTH } from 'dashboard/helper/workflowHealth';

const isCampaignActive = campaign => {
  if (!campaign || campaign.campaign_status === 'completed') return false;
  if (campaign.is_drip) return campaign.drip_status === 'running';
  if (campaign.campaign_type === 'ongoing') return campaign.enabled === true;
  return (
    campaign.campaign_status === 'active' ||
    campaign.campaign_status === 'processing'
  );
};

export function useHubLiveMeta(visibleWorlds) {
  const { t } = useI18n();
  const store = useStore();
  const { hasUnread: hasUnreadRelease, latestRelease } = useReleaseNotes();

  const conversationStats = useMapGetter('conversationStats/getStats');
  const allConversations = useMapGetter('getAllConversations');
  const currentUserId = useMapGetter('getCurrentUserID');
  const access = useHubDataAccess();

  const tasksToday = ref(null);
  const activeCampaignCount = ref(null);
  const attentionCount = ref(null);
  const aiCenterSummary = ref(null);

  const hasWorld = id =>
    (visibleWorlds.value || []).some(world => world.id === id);
  // A área pode estar visível por outro destino (Automações aparece para o
  // atendente por automation_logs), mas a API só responde a quem vê o destino.
  const hasDestination = (worldId, destinationId) =>
    (visibleWorlds.value || []).some(
      world =>
        world.id === worldId &&
        (world.destinations || []).some(
          destination => destination.id === destinationId
        )
    );

  const aiCenterPending = computed(
    () => Number(aiCenterSummary.value?.pending) || 0
  );

  const mineCount = computed(() => conversationStats.value?.mineCount || 0);
  // Sem ninguém: o mesmo meta traz a fila da equipe. Mostrado ao lado do
  // "com você" para o atendente saber qual das duas pede ação.
  const unassignedCount = computed(
    () => conversationStats.value?.unAssignedCount || 0
  );

  // Mesmo universo do número ao lado ("com você"): só as conversas ativas
  // atribuídas a quem está logado. Antes varria tudo o que as listas visitadas
  // deixaram no store, inclusive conversas de outros atendentes.
  const maxWaitMinutes = computed(() => {
    const waits = (allConversations.value || [])
      .filter(
        chat =>
          Number(chat.waiting_since) > 0 &&
          chat.status !== 'resolved' &&
          currentUserId.value &&
          chat.meta?.assignee?.id === currentUserId.value
      )
      .map(chat => waitingMinutesFromEpoch(chat.waiting_since));
    if (!waits.length) return null;
    return Math.max(0, ...waits);
  });

  const adminAreaCount = computed(() => {
    const system = (visibleWorlds.value || []).find(
      world => world.id === 'system'
    );
    if (!system) return null;
    const count = system.destinations.filter(destination =>
      ['help_center', 'settings'].includes(destination.id)
    ).length;
    return count > 0 ? count : null;
  });

  const liveMetaByWorld = computed(() => {
    const meta = {};

    if (hasWorld('sales') && tasksToday.value > 0) {
      meta.sales = t('HUB.LIVE.TASKS_TODAY', tasksToday.value, {
        count: tasksToday.value,
      });
    }

    if (hasWorld('marketing') && activeCampaignCount.value > 0) {
      meta.marketing = t(
        'HUB.LIVE.ACTIVE_CAMPAIGNS',
        activeCampaignCount.value,
        { count: activeCampaignCount.value }
      );
    }

    if (hasWorld('automation') && attentionCount.value > 0) {
      meta.automation = t('HUB.LIVE.FLOWS_ATTENTION', attentionCount.value, {
        count: attentionCount.value,
      });
    }

    if (hasWorld('ai_center')) {
      // Mesmo total da Central: a soma dos contadores das abas.
      const pending = aiCenterPending.value;
      meta.ai_center = aiCenterSummary.value
        ? t('HUB.LIVE.AI_CENTER', { count: pending }, pending)
        : '';
    }

    // Novidade nao lida ganha do numero de areas de administracao: e a
    // informacao que muda e que a pessoa precisa ver.
    if (hasWorld('system') && hasUnreadRelease.value && latestRelease.value) {
      meta.system = t('HUB.LIVE.RELEASE_NOTES', {
        version: latestRelease.value.version,
      });
    } else if (hasWorld('system') && adminAreaCount.value > 0) {
      meta.system = t('HUB.LIVE.ADMIN_AREAS', adminAreaCount.value, {
        count: adminAreaCount.value,
      });
    }

    return meta;
  });

  const liveSignalByWorld = computed(() => ({
    automation: Boolean(attentionCount.value > 0),
    ai_center: aiCenterPending.value > 0,
    // Ponto, nunca contador: no primeiro deploy todo mundo esta sem a chave
    // e um numero alto assustaria sem motivo.
    system: Boolean(hasWorld('system') && hasUnreadRelease.value),
  }));

  onMounted(() => {
    // As leituras abaixo são as mesmas da Central V2 (useHubOverview), que
    // monta junto: dividem a request em voo em vez de repetir o GET. O recorte
    // do meta (ativas, igual ao chip "Minhas") mora em
    // refreshHubConversationStats.
    refreshHubConversationStats(store).catch(() => {});

    const accountId = store.getters.getCurrentAccountId;

    if (hasWorld('sales')) {
      loadHubTaskNotifications(store)
        .then(() => {
          tasksToday.value = store.getters['tasks/getTodayCount'];
        })
        .catch(() => {
          tasksToday.value = null;
        });
    }

    // Marketing aparece para o agente por Grupos ou Sequências, mas a lista de
    // campanhas é de admin ou campaigns_view: sem isso o GET voltava 401.
    if (hasWorld('marketing') && access.value.campaigns) {
      loadHubCampaigns(accountId)
        .then(records => {
          activeCampaignCount.value = records.filter(isCampaignActive).length;
        })
        .catch(() => {
          activeCampaignCount.value = null;
        });
    }

    // Fluxos são só de administrador: sem o destino, o GET voltava 401.
    if (
      access.value.flows &&
      hasDestination('automation', 'flow_automations')
    ) {
      loadHubFlows(accountId)
        .then(flows => {
          attentionCount.value = flows.filter(flow =>
            ATTENTION_HEALTH.has(flow.health)
          ).length;
        })
        .catch(() => {
          attentionCount.value = null;
        });
    }

    if (hasWorld('ai_center')) {
      AiCenterAPI.getSummary(store.getters.getCurrentAccountId)
        .then(response => {
          aiCenterSummary.value = response.data?.data || response.data || null;
        })
        .catch(() => {
          aiCenterSummary.value = null;
        });
    }
  });

  return {
    mineCount,
    unassignedCount,
    maxWaitMinutes,
    // O rodapé do Hub acende o ponto de não lido no link de Novidades.
    hasUnreadRelease,
    liveMetaByWorld,
    liveSignalByWorld,
  };
}
